<template>
  <div class="netskope-page">
    <div class="page-header">
      <div class="header-left">
        <img src="@/assets/netskope.jpg" alt="Netskope" class="netskope-logo" />
        <div class="header-text">
          <h1>Netskope Baseline</h1>
          <p>Deploy Netskope configuration components to a selected tenant</p>
        </div>
      </div>
    </div>

    <div class="content-card">
      <div class="section-header">
        <h2>Pipeline Selection</h2>
        <p v-if="pipelinesLoading" class="pipeline-status">Loading pipelines...</p>
        <p v-else-if="pipelines.length === 0" class="pipeline-status error">No Netskope pipelines found</p>
      </div>
      <div class="pipeline-row">
        <div class="pipeline-field">
          <label for="pipeline-select">Target Client Pipeline</label>
          <select
            id="pipeline-select"
            v-model="selectedPipelineId"
            class="pipeline-dropdown"
            :disabled="pipelinesLoading"
          >
            <option value="">-- Select a client pipeline --</option>
            <option
              v-for="pipeline in pipelines"
              :key="pipeline.id"
              :value="pipeline.id"
            >
              {{ pipeline.name }} ({{ formatFolder(pipeline.folder) }})
            </option>
          </select>
        </div>
        <button class="btn-refresh-pipelines" @click="fetchPipelines" :disabled="pipelinesLoading">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
          Refresh
        </button>
      </div>
    </div>

    <div class="content-card">
      <div class="section-header">
        <h2>Baseline Components</h2>
        <p v-if="deploymentMessage.text" :class="['deployment-status', deploymentMessage.type]">
          {{ deploymentMessage.text }}
        </p>
      </div>

      <div class="select-all-row">
        <label class="checkbox-label">
          <input type="checkbox" :checked="allSelected" @change="toggleSelectAll" />
          <span>Select All</span>
        </label>
        <span class="selection-count">{{ selectedComponents.length }} of {{ components.length }} selected</span>
      </div>

      <div class="components-list">
        <div
          v-for="component in components"
          :key="component.key"
          class="component-item"
          :class="{ selected: selectedComponents.includes(component.key) }"
        >
          <label class="component-label">
            <input
              type="checkbox"
              :value="component.key"
              v-model="selectedComponents"
              :disabled="deploying"
            />
            <div class="component-info">
              <div class="component-name">{{ component.name }}</div>
              <div class="component-desc">{{ component.description }}</div>
              <div class="component-meta">
                <span class="meta-badge">{{ component.impact }} Impact</span>
                <span class="meta-path">{{ component.path }}</span>
    </div>

    <!-- Component Detail Modal -->
    <div v-if="detailModal.component" class="modal-overlay" @click.self="closeDetailModal">
      <div class="detail-modal">
        <div class="modal-header">
          <h2>{{ detailModal.component.name }}</h2>
          <button @click="closeDetailModal" class="modal-close">&times;</button>
        </div>

        <div class="modal-body">
          <div class="detail-section">
            <div class="detail-row">
              <span class="detail-label">Description</span>
              <span class="detail-value">{{ detailModal.component.description }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Path</span>
              <code class="detail-path">{{ detailModal.component.path }}</code>
            </div>
            <div class="detail-row">
              <span class="detail-label">Impact</span>
              <span :class="['impact-badge', detailModal.component.impact.toLowerCase()]">{{ detailModal.component.impact }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Type</span>
              <span class="detail-value">Directory (all JSON files are deployed)</span>
            </div>
          </div>

          <div v-if="detailModal.loading" class="modal-loading">
            <div class="spinner"></div>
            <p>Loading configuration files...</p>
          </div>

          <div v-else-if="detailModal.error" class="modal-error">
            <p>{{ detailModal.error }}</p>
          </div>

          <div v-else-if="detailModal.files.length" class="detail-files">
            <h3>Configuration Files ({{ detailModal.files.length }})</h3>
            <div
              v-for="(file, idx) in detailModal.files"
              :key="idx"
              class="file-item"
            >
              <div class="file-header" @click="toggleFileExpanded(idx)">
                <svg class="file-expand-icon" :class="{ expanded: expandedFiles.has(idx) }" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
                <span class="file-name">{{ file.name }}</span>
                <span class="file-size">{{ formatJsonSize(file.content) }}</span>
              </div>
              <div v-if="expandedFiles.has(idx)" class="file-content">
                <pre class="json-preview">{{ prettyJson(file.content) }}</pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
            </div>
          </label>
          <button
            class="btn-info"
            :disabled="infoLoading[component.key]"
            :title="infoLoading[component.key] ? 'Loading...' : 'View details'"
            @click.stop="viewComponentDetails(component)"
          >
            <svg v-if="infoLoading[component.key]" class="spinner-icon" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" stroke-dasharray="32" stroke-linecap="round"/>
            </svg>
            <span v-else>ℹ️</span>
          </button>
        </div>
      </div>

      <div class="deploy-actions">
        <button
          class="btn-deploy"
          :disabled="!canDeploy"
          @click="showConfirmModal = true"
        >
          <svg v-if="deploying" class="spinner-icon" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" stroke-dasharray="32" stroke-linecap="round"/>
          </svg>
          {{ deploying ? 'Deploying...' : `Deploy ${selectedComponents.length} Component${selectedComponents.length !== 1 ? 's' : ''}` }}
        </button>
        <p v-if="!selectedPipelineId" class="deploy-hint">Select a target client pipeline above to enable deployment</p>
        <p v-else-if="selectedComponents.length === 0" class="deploy-hint">Select at least one component to deploy</p>
      </div>
    </div>

    <div class="content-card">
      <div class="section-header">
        <h2>Deployment History</h2>
      </div>
      <div v-if="deploymentHistory.length === 0" class="empty-state">
        <p>No deployments yet. Select a pipeline and components above to start.</p>
      </div>
      <div v-else class="history-list">
        <div
          v-for="(entry, index) in deploymentHistory"
          :key="index"
          class="history-item"
        >
          <span :class="['status-dot', entry.status]"></span>
          <div class="history-details">
            <span class="history-client">{{ entry.client }}</span>
            <span class="history-components">{{ entry.components.join(', ') }}</span>
          </div>
          <span class="history-time">{{ formatTime(entry.timestamp) }}</span>
          <span :class="['status-badge', entry.status]">{{ entry.status }}</span>
        </div>
      </div>
    </div>

    <!-- Deployment Confirmation Modal -->
    <div v-if="showConfirmModal" class="modal-overlay" @click.self="showConfirmModal = false">
      <div class="confirm-modal">
        <div class="modal-header">
          <h2>Confirm Deployment</h2>
          <button @click="showConfirmModal = false" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <p class="confirm-intro">You are about to deploy the following Netskope configuration to a client tenant:</p>

          <div class="confirm-detail">
            <span class="confirm-label">Target Client</span>
            <span class="confirm-value">{{ selectedPipelineName }}</span>
          </div>
          <div class="confirm-detail">
            <span class="confirm-label">Components</span>
            <div class="confirm-component-list">
              <div v-for="name in selectedComponentNames" :key="name" class="confirm-component-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                {{ name }}
              </div>
            </div>
          </div>

          <div class="confirm-warning">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d97706" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="11" y1="15" x2="11" y2="15"/><line x1="12" y1="15" x2="12" y2="15"/></svg>
            <span>This will trigger a pipeline run on the selected client. Please verify the target client is correct before proceeding.</span>
          </div>

          <div class="confirm-actions">
            <button @click="showConfirmModal = false" class="btn-cancel">Cancel</button>
            <button @click="confirmDeploy" class="btn-confirm-deploy">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
              Deploy Now
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  runCustomBaselinePipeline,
  getPipelineStatus,
  fetchPipelinesWithFolderFilter,
  fetchDirectoryContents,
  fetchJsonFile,
} from "@/azure-devops-api.js";
import { getAccessToken } from "@/services/auth-utils.js";

const REPO_ID = "62b297c6-760c-4250-a926-52b8850e6d5f";

export default {
  name: "NetskopeBaseline",

  data() {
    return {
      components: [
        {
          key: "url-lists",
          name: "Import Netskope URL Lists",
          description: "Creates and deploys Netskope URL lists to the tenant",
          path: "/Netskope/Baseline/URL_Lists",
          impact: "Medium",
        },
        {
          key: "custom-categories",
          name: "Import Netskope Custom Categories",
          description: "Creates and deploys Netskope Custom Categories to the tenant",
          path: "/Netskope/Baseline/CustomCategories",
          impact: "Medium",
        },
      ],
      pipelines: [],
      pipelinesLoading: false,
      selectedPipelineId: "",
      selectedComponents: [],
      deploying: false,
      deploymentMessage: { text: "", type: "" },
      deploymentHistory: [],
      pollingTimer: null,
      infoLoading: {},
      detailModal: {
        component: null,
        files: [],
        loading: false,
        error: null,
      },
      expandedFiles: new Set(),
      showConfirmModal: false,
    };
  },

  computed: {
    allSelected() {
      return this.components.length > 0 && this.selectedComponents.length === this.components.length;
    },
    canDeploy() {
      return (
        !this.deploying &&
        this.selectedPipelineId &&
        this.selectedComponents.length > 0
      );
    },
    selectedPipelineName() {
      const pipeline = this.pipelines.find((p) => p.id === this.selectedPipelineId);
      return pipeline ? pipeline.name : "Unknown";
    },
    selectedComponentNames() {
      return this.components
        .filter((c) => this.selectedComponents.includes(c.key))
        .map((c) => c.name);
    },
  },

  mounted() {
    this.fetchPipelines();
  },

  beforeUnmount() {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
    }
  },

  methods: {
    async fetchPipelines() {
      this.pipelinesLoading = true;
      try {
        const accessToken = await getAccessToken();

        // First try with "Deploy Baseline Tenant Configuration" folder filter (same as TenantBaselineOptions custom tab)
        const allPipelines = await fetchPipelinesWithFolderFilter(
          accessToken,
          "Aegis",
          "Deploy Baseline Tenant Configuration"
        );

        // Filter to only show client-specific pipelines (folder length > 3 means nested under a client)
        this.pipelines = allPipelines.filter(pipeline => {
          const folder = (pipeline.folder || "").replace("\\", "").trim();
          return folder.length > 3;
        });

        if (this.pipelines.length > 0) {
          this.selectedPipelineId = this.pipelines[0].id;
        }
      } catch (error) {
        console.error("Error fetching pipelines:", error);
        this.deploymentMessage = { text: "Failed to load pipelines", type: "error" };
      } finally {
        this.pipelinesLoading = false;
      }
    },

    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedComponents = [];
      } else {
        this.selectedComponents = this.components.map((c) => c.key);
      }
    },

    confirmDeploy() {
      this.showConfirmModal = false;
      this.deploySelected();
    },

    async deploySelected() {
      if (!this.canDeploy) return;

      this.deploying = true;
      this.deploymentMessage = { text: "Starting deployment...", type: "info" };

      const selectedNames = this.components
        .filter((c) => this.selectedComponents.includes(c.key))
        .map((c) => c.name);

      try {
        const accessToken = await getAccessToken();

        const selectedPipeline = this.pipelines.find(
          (p) => p.id === this.selectedPipelineId
        );
        if (!selectedPipeline) {
          throw new Error("Selected pipeline not found");
        }

        const projectId = selectedPipeline._links.self.href.split("/")[4];

        const componentsJson = JSON.stringify({
          components: selectedNames,
        });

        const runId = await runCustomBaselinePipeline(
          accessToken,
          projectId,
          this.selectedPipelineId,
          componentsJson,
        );

        if (runId) {
          this.deploymentMessage = {
            text: `Deployment started for ${selectedNames.length} component${selectedNames.length !== 1 ? 's' : ''}`,
            type: "success",
          };

          this.addHistoryEntry(selectedPipeline.name, selectedNames, "running");

          this.pollStatus(accessToken, projectId, this.selectedPipelineId, runId, selectedPipeline.name);
        } else {
          throw new Error("Failed to trigger pipeline");
        }
      } catch (error) {
        console.error("Deployment error:", error);
        this.deploymentMessage = {
          text: `Deployment failed: ${error.message}`,
          type: "error",
        };
        this.addHistoryEntry(
          this.pipelines.find((p) => p.id === this.selectedPipelineId)?.name || "Unknown",
          selectedNames,
          "failed"
        );
        this.deploying = false;
      }
    },

    pollStatus(accessToken, projectId, pipelineId, runId, clientName) {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer);
        this.pollingTimer = null;
      }
      this.pollingTimer = setInterval(async () => {
        try {
          const runData = await getPipelineStatus(accessToken, projectId, pipelineId, runId);
          const { state, result } = runData;

          if (state === "completed") {
            clearInterval(this.pollingTimer);
            this.pollingTimer = null;

            if (result === "canceled") {
              this.deploymentMessage = {
                text: `Deployment to ${clientName} was cancelled`,
                type: "warning",
              };
              this.updateHistoryEntry(clientName, "cancelled");
            } else if (result === "failed") {
              this.deploymentMessage = {
                text: `Deployment to ${clientName} failed`,
                type: "error",
              };
              this.updateHistoryEntry(clientName, "failed");
            } else {
              this.deploymentMessage = {
                text: `Deployment to ${clientName} completed successfully`,
                type: "success",
              };
              this.updateHistoryEntry(clientName, "completed");
            }

            this.deploying = false;
          }
        } catch (error) {
          clearInterval(this.pollingTimer);
          this.pollingTimer = null;
          this.updateHistoryEntry(clientName, "error");
          this.deploying = false;
        }
      }, 5000);
    },

    addHistoryEntry(client, components, status) {
      this.deploymentHistory.unshift({
        client,
        components: [...components],
        status,
        timestamp: new Date(),
      });
    },

    updateHistoryEntry(client, status) {
      const entry = this.deploymentHistory.find(
        (e) => e.client === client && e.status === "running"
      );
      if (entry) {
        entry.status = status;
      }
    },

    formatFolder(folder) {
      return folder.replace("\\", "").trim();
    },

    formatTime(date) {
      return new Date(date).toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },

    async viewComponentDetails(component) {
      this.detailModal.component = component;
      this.detailModal.files = [];
      this.detailModal.loading = true;
      this.detailModal.error = null;
      this.expandedFiles.clear();

      try {
        this.infoLoading[component.key] = true;
        const accessToken = await getAccessToken();

        const items = await fetchDirectoryContents(accessToken, REPO_ID, component.path);

        if (!items || !items.length) {
          this.detailModal.error = 'No configuration files found in this directory';
          this.detailModal.loading = false;
          return;
        }

        const jsonFiles = items.filter((item) => {
          if (!item || !item.path) return false;
          const name = item.path.split('/').pop();
          return name && name.endsWith('.json');
        });

        const files = [];
        for (const file of jsonFiles) {
          const fileName = file.path ? file.path.split('/').pop() : 'unknown.json';
          try {
            const result = await fetchJsonFile(accessToken, REPO_ID, file.path);
            files.push({
              name: fileName,
              path: file.path,
              content: result.content,
            });
          } catch (err) {
            console.error(`Failed to load ${fileName}:`, err);
          }
        }

        this.detailModal.files = files;
        this.detailModal.loading = false;
      } catch (error) {
        console.error("Error loading component details:", error);
        this.detailModal.error = `Failed to load configuration: ${error.message}`;
        this.detailModal.loading = false;
      } finally {
        this.infoLoading[component.key] = false;
      }
    },

    closeDetailModal() {
      this.detailModal.component = null;
      this.detailModal.files = [];
      this.expandedFiles.clear();
    },

    toggleFileExpanded(idx) {
      if (this.expandedFiles.has(idx)) {
        this.expandedFiles.delete(idx);
      } else {
        this.expandedFiles.add(idx);
      }
    },

    prettyJson(raw) {
      try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        return JSON.stringify(parsed, null, 2);
      } catch {
        return raw;
      }
    },

    formatJsonSize(content) {
      try {
        const parsed = typeof content === 'string' ? JSON.parse(content) : content;
        const keys = Object.keys(parsed);
        const arrLen = Array.isArray(parsed) ? parsed.length : 0;
        if (arrLen) return `${arrLen} items`;
        return `${keys.length} keys`;
      } catch {
        return '';
      }
    },

    getAccessToken,
  },
};
</script>

<style scoped>
.netskope-page {
  padding: 24px;
}

.page-header {
  margin-bottom: 24px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.netskope-logo {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  object-fit: contain;
}

.header-text h1 {
  margin: 0 0 4px 0;
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
}

.header-text p {
  margin: 0;
  font-size: 14px;
  color: #64748b;
}

.content-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border: 1px solid #e2e8f0;
  margin-bottom: 20px;
  overflow: hidden;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
}

.section-header h2 {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #334155;
}

.deployment-status {
  margin: 0;
  font-size: 13px;
  font-weight: 500;
  padding: 4px 12px;
  border-radius: 6px;
}

.deployment-status.success { background: #f0fdf4; color: #166534; }
.deployment-status.error { background: #fef2f2; color: #991b1b; }
.deployment-status.info { background: #eff6ff; color: #1e40af; }

.pipeline-status {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}
.pipeline-status.error { color: #dc2626; }

/* Pipeline Selection */
.pipeline-row {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  padding: 12px 20px 20px;
}

.pipeline-field {
  flex: 1;
}

.pipeline-field label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}

.pipeline-dropdown {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #334155;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s;
}

.pipeline-dropdown:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
}

.pipeline-dropdown:disabled {
  background: #f8fafc;
  cursor: not-allowed;
}

.btn-refresh-pipelines {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}

.btn-refresh-pipelines:hover:not(:disabled) {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.btn-refresh-pipelines:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Select All Row */
.select-all-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-weight: 500;
  color: #334155;
}

.checkbox-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #3b82f6;
  cursor: pointer;
}

.selection-count {
  color: #94a3b8;
  font-size: 12px;
}

/* Components List */
.components-list {
  padding: 8px 20px 12px;
}

.component-item {
  display: flex;
  align-items: center;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 10px;
  transition: all 0.15s;
}

.component-item:last-child {
  margin-bottom: 0;
}

.component-item:hover {
  border-color: #cbd5e1;
}

.component-item.selected {
  border-color: #93c5fd;
  background: #eff6ff;
}

.component-label {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  cursor: pointer;
  width: 100%;
}

.component-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #3b82f6;
  cursor: pointer;
  margin-top: 2px;
  flex-shrink: 0;
}

.component-label input[type="checkbox"]:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.component-info {
  flex: 1;
}

.component-name {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 2px;
}

.component-desc {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 6px;
}

.component-meta {
  display: flex;
  gap: 12px;
}

.meta-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  background: #f1f5f9;
  color: #64748b;
  text-transform: uppercase;
}

.meta-path {
  font-size: 11px;
  color: #94a3b8;
  font-family: monospace;
}

/* Deploy Actions */
.deploy-actions {
  padding: 12px 20px 20px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.btn-deploy {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 24px;
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-deploy:hover:not(:disabled) {
  background: #1d4ed8;
}

.btn-deploy:disabled {
  background: #93c5fd;
  cursor: not-allowed;
}

.spinner-icon {
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.deploy-hint {
  margin: 0;
  font-size: 12px;
  color: #94a3b8;
}

/* History */
.empty-state {
  padding: 40px 20px;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
}

.history-list {
  padding: 8px 20px 16px;
}

.history-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;
}

.history-item:last-child {
  border-bottom: none;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-dot.completed { background: #22c55e; }
.status-dot.running { background: #f59e0b; animation: pulse 1.5s infinite; }
.status-dot.failed { background: #ef4444; }
.status-dot.error { background: #ef4444; }
.status-dot.cancelled { background: #94a3b8; }

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

.history-details {
  flex: 1;
  min-width: 0;
}

.history-client {
  font-weight: 600;
  color: #334155;
  display: block;
  margin-bottom: 1px;
}

.history-components {
  color: #94a3b8;
  font-size: 12px;
}

.history-time {
  color: #94a3b8;
  font-size: 12px;
  white-space: nowrap;
}

.status-badge {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 4px;
}

.status-badge.completed { background: #f0fdf4; color: #166534; }
.status-badge.running { background: #fffbeb; color: #92400e; }
.status-badge.failed,
.status-badge.error { background: #fef2f2; color: #991b1b; }
.status-badge.cancelled { background: #f1f5f9; color: #64748b; }

/* Info Button */
.btn-info {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  color: #64748b;
  transition: all 0.15s;
  flex-shrink: 0;
  margin-left: 8px;
  margin-right: 12px;
}

.btn-info:hover:not(:disabled) {
  background: #f1f5f9;
  border-color: #cbd5e1;
  color: #334155;
}

.btn-info:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Detail Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 24px;
}

.detail-modal {
  background: #fff;
  border-radius: 12px;
  max-width: 800px;
  width: 100%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
}

.modal-close {
  background: none;
  border: none;
  font-size: 24px;
  color: #94a3b8;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  transition: color 0.15s;
}

.modal-close:hover {
  color: #475569;
}

.modal-body {
  padding: 20px 24px;
  overflow-y: auto;
}

.detail-section {
  margin-bottom: 20px;
}

.detail-row {
  display: flex;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;
}

.detail-label {
  font-weight: 600;
  color: #64748b;
  min-width: 100px;
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.5px;
}

.detail-value {
  color: #334155;
}

.detail-path {
  font-size: 12px;
  background: #f1f5f9;
  padding: 2px 8px;
  border-radius: 4px;
  color: #475569;
}

.impact-badge {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 4px;
}

.impact-badge.medium { background: #fef9c3; color: #854d0e; }
.impact-badge.high { background: #fee2e2; color: #991b1b; }
.impact-badge.low { background: #f0fdf4; color: #166534; }

.modal-loading {
  text-align: center;
  padding: 40px 20px;
  color: #94a3b8;
}

.modal-loading .spinner {
  width: 24px;
  height: 24px;
  border: 3px solid #e2e8f0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 12px;
}

.modal-error {
  text-align: center;
  padding: 20px;
  color: #dc2626;
  font-size: 14px;
}

.detail-files h3 {
  font-size: 14px;
  font-weight: 600;
  color: #475569;
  margin: 0 0 12px 0;
}

.file-item {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  margin-bottom: 8px;
  overflow: hidden;
}

.file-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #f8fafc;
  cursor: pointer;
  font-size: 13px;
  transition: background 0.15s;
}

.file-header:hover {
  background: #f1f5f9;
}

.file-expand-icon {
  transition: transform 0.2s;
  color: #94a3b8;
  flex-shrink: 0;
}

.file-expand-icon.expanded {
  transform: rotate(90deg);
}

.file-name {
  font-weight: 500;
  color: #334155;
  font-family: monospace;
  font-size: 12px;
}

.file-size {
  margin-left: auto;
  font-size: 11px;
  color: #94a3b8;
}

.file-content {
  border-top: 1px solid #e2e8f0;
}

.json-preview {
  margin: 0;
  padding: 12px 16px;
  background: #1e293b;
  color: #e2e8f0;
  font-size: 12px;
  line-height: 1.5;
  overflow-x: auto;
  max-height: 400px;
  overflow-y: auto;
  font-family: 'Courier New', monospace;
  white-space: pre;
}

/* Confirm Modal */
.confirm-modal {
  background: #fff;
  border-radius: 12px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}

.confirm-intro {
  margin: 0 0 16px 0;
  font-size: 14px;
  color: #475569;
}

.confirm-detail {
  display: flex;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;
}

.confirm-label {
  font-weight: 600;
  color: #64748b;
  min-width: 90px;
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.5px;
  padding-top: 2px;
}

.confirm-value {
  color: #1e293b;
  font-weight: 500;
}

.confirm-component-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.confirm-component-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #334155;
  font-size: 13px;
}

.confirm-warning {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 16px;
  padding: 12px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  font-size: 13px;
  color: #92400e;
  line-height: 1.5;
}

.confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.btn-cancel {
  padding: 8px 16px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-cancel:hover {
  background: #f1f5f9;
}

.btn-confirm-deploy {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  background: #dc2626;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-confirm-deploy:hover {
  background: #b91c1c;
}
</style>
